// Image merge tags: an image on an inner page can carry `mergeTag: '<tagName>'`. In the viewer the
// recipient's value for that tag (an https image URL) replaces the image; otherwise the placed image
// (downloadUrl / imageUrl) is the fallback. The cover (page index 0) is personalized only for dynamic
// covers (`includeCover`); a static cover is a plain image.

export const MAX_IMAGE_URL_LENGTH = 2048;

export const isValidImageUrl = (value: unknown): value is string => {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	if (!trimmed || trimmed.length > MAX_IMAGE_URL_LENGTH) return false;
	try {
		return new URL(trimmed).protocol === 'https:';
	} catch {
		return false;
	}
};

export const proxiedImageUrl = (url: string): string =>
	`${process.env.REACT_APP_API_URL || ''}/proxy/image?url=${encodeURIComponent(url)}`;

/** Returns a copy of `pages` with `personalizedUrl` set on bound images whose tag value is a valid image URL. */
export const resolveImageMergeTags = (pages: any[], values: Record<string, unknown>, includeCover = false): any[] =>
	(pages || []).map((page, pageIndex) => {
		if ((pageIndex === 0 && !includeCover) || !page?.images?.length) return page;
		return {
			...page,
			images: page.images.map((image: any) => {
				if (!image?.mergeTag) return image;
				const value = values?.[image.mergeTag];
				return isValidImageUrl(value) ? { ...image, personalizedUrl: value.trim() } : image;
			}),
		};
	});

/** Unique merge tags bound to images on inner pages (and the cover with `includeCover`), in first-seen order. */
export const getBoundImageTags = (pages: any[], includeCover = false): string[] => {
	const tags = new Set<string>();
	(pages || []).forEach((page, pageIndex) => {
		if (pageIndex === 0 && !includeCover) return;
		page?.images?.forEach((image: any) => image?.mergeTag && tags.add(image.mergeTag));
	});
	return Array.from(tags);
};

/** Konva crop rect that fills a boxW x boxH box with an imgW x imgH image, centred, aspect preserved. */
export const coverCrop = (imgW: number, imgH: number, boxW: number, boxH: number) => {
	const boxRatio = boxW / boxH;
	if (imgW / imgH > boxRatio) {
		const width = imgH * boxRatio;
		return { x: (imgW - width) / 2, y: 0, width, height: imgH };
	}
	const height = imgW / boxRatio;
	return { x: 0, y: (imgH - height) / 2, width: imgW, height };
};

/** Copy of `pages` with `showMergeTagBadge` on bound inner-page images (sender preview without prospect data). */
export const markImageMergeTags = (pages: any[], includeCover = false): any[] =>
	(pages || []).map((page, pageIndex) =>
		(pageIndex === 0 && !includeCover) || !page?.images?.length
			? page
			: { ...page, images: page.images.map((image: any) => (image?.mergeTag ? { ...image, showMergeTagBadge: true } : image)) }
	);
